/**
 * SpecIcon — renders the `icon` field of an AI-authored panel spec as a
 * lucide icon, never as an emoji glyph.
 *
 * Specs written before the de-emoji pass carry a single emoji ("📈", "⚠️");
 * newer specs may name a lucide icon ("trending-up", "alert-triangle").
 * Known emoji map to their lucide equivalent; anything unrecognised renders
 * nothing rather than a stray glyph. User-entered content elsewhere is never
 * touched — this is only for decorative spec icons.
 */
import {
  Activity, AlertTriangle, Archive, BarChart3, Bell, Bookmark, Bot, Briefcase, Building2, Calendar, Check, CheckCircle2,
  Clock, Crosshair, DollarSign, FileText, Flag, Flame, Globe, Handshake, Heart, Info, Landmark, Lightbulb, Link2,
  Lock, Mail, Map, MapPin, Megaphone, MessageSquare, Mic, Newspaper, Pause, Pencil, Phone, PieChart, Rocket, Scale,
  ScrollText, Search, Settings, Shield, Shuffle, Sparkles, Star, Target, ThumbsDown, ThumbsUp, TrendingDown, TrendingUp, Trophy, User,
  Users, Vote, X, XCircle, Zap, type LucideIcon,
} from "lucide-react";

const EMOJI: Record<string, LucideIcon> = {
  "📈": TrendingUp, "📉": TrendingDown, "📊": BarChart3, "🥧": PieChart, "⚠": AlertTriangle, "🚨": AlertTriangle,
  "❗": AlertTriangle, "❓": Info, "ℹ": Info, "💡": Lightbulb, "✅": CheckCircle2, "✔": Check, "✓": Check, "☑": CheckCircle2,
  "❌": XCircle, "✗": X, "✖": X, "⏸": Pause, "🔥": Flame, "⚡": Zap, "✨": Sparkles, "⭐": Star, "🌟": Star,
  "🎯": Target, "🏆": Trophy, "🚀": Rocket, "📅": Calendar, "🗓": Calendar, "⏰": Clock, "⏱": Clock, "🕐": Clock,
  "📰": Newspaper, "🗞": Newspaper, "📄": FileText, "📝": Pencil, "📋": FileText, "📑": FileText, "📁": Archive,
  "🗂": Archive, "📌": MapPin, "📍": MapPin, "🗺": Map, "🌎": Globe, "🌍": Globe, "🌐": Globe, "🏛": Landmark,
  "🏢": Building2, "🏦": Landmark, "⚖": Scale, "🗳": Vote, "🤝": Handshake, "👥": Users, "👤": User, "🧑": User,
  "🤖": Bot, "💬": MessageSquare, "🗨": MessageSquare, "📣": Megaphone, "📢": Megaphone, "🎤": Mic, "📞": Phone,
  "✉": Mail, "📧": Mail, "📨": Mail, "🔔": Bell, "🔍": Search, "🔎": Search, "🔗": Link2, "🔒": Lock, "🛡": Shield,
  "⚙": Settings, "🔧": Settings, "💰": DollarSign, "💵": DollarSign, "💼": Briefcase, "🚩": Flag, "❤": Heart,
  "👍": ThumbsUp, "👎": ThumbsDown, "🔖": Bookmark, "📚": Bookmark, "🎙": Mic, "📶": Activity, "💥": Zap, "🧭": Crosshair,
  "📜": ScrollText, "🔀": Shuffle,
};

const NAMED: Record<string, LucideIcon> = {
  "trending-up": TrendingUp, "trending-down": TrendingDown, "bar-chart": BarChart3, "chart": BarChart3, "pie-chart": PieChart,
  "alert": AlertTriangle, "alert-triangle": AlertTriangle, "warning": AlertTriangle, "info": Info, "lightbulb": Lightbulb,
  "check": Check, "check-circle": CheckCircle2, "x": X, "x-circle": XCircle, "pause": Pause, "flame": Flame, "zap": Zap,
  "sparkles": Sparkles, "star": Star, "target": Target, "trophy": Trophy, "rocket": Rocket, "calendar": Calendar,
  "clock": Clock, "newspaper": Newspaper, "news": Newspaper, "file": FileText, "file-text": FileText, "pencil": Pencil,
  "archive": Archive, "map-pin": MapPin, "map": Map, "globe": Globe, "landmark": Landmark, "building": Building2,
  "scale": Scale, "vote": Vote, "handshake": Handshake, "users": Users, "user": User, "bot": Bot, "message": MessageSquare,
  "megaphone": Megaphone, "mic": Mic, "phone": Phone, "mail": Mail, "bell": Bell, "search": Search, "link": Link2,
  "lock": Lock, "shield": Shield, "settings": Settings, "dollar": DollarSign, "briefcase": Briefcase, "flag": Flag,
  "heart": Heart, "thumbs-up": ThumbsUp, "thumbs-down": ThumbsDown, "bookmark": Bookmark, "activity": Activity,
  "scroll": ScrollText, "scroll-text": ScrollText, "shuffle": Shuffle,
};

/** The lucide icon for a spec icon string, or null when it should not render. */
export function resolveSpecIcon(icon: string | null | undefined): LucideIcon | null {
  if (!icon) return null;
  const trimmed = icon.trim();
  const named = NAMED[trimmed.toLowerCase()];
  if (named) return named;
  // Strip variation selectors / skin tones before the emoji lookup.
  const base = trimmed.replace(/[︎️‍]|\uD83C[\uDFFB-\uDFFF]/g, "");
  return EMOJI[base] ?? EMOJI[Array.from(base)[0] ?? ""] ?? null;
}

/** True when a string is (mostly) pictographic and should not be shown as text. */
export function isEmojiGlyph(value: string | null | undefined): boolean {
  return Boolean(value && /\p{Extended_Pictographic}/u.test(value) && value.trim().length <= 8);
}

export function SpecIcon({ icon, size = 16, className = "" }: { icon?: string | null; size?: number; className?: string }) {
  const Icon = resolveSpecIcon(icon);
  if (!Icon) return null;
  return <Icon size={size} className={className} aria-hidden="true" />;
}
